import { z } from 'zod';
import { API_CONFIG } from '../config/api';
import { ApiError, NetworkError, TimeoutError, handleApiError } from './errors';
import { logger } from '../utils/logger';
import { deduplicateRequest } from './requestDeduplication';
import { validateResponse, ValidationError } from './validation';

interface RequestOptions extends RequestInit {
  timeout?: number;
  retries?: number;
  validate?: z.ZodSchema<any>;
  deduplicate?: boolean;
  deduplicationKey?: string;
  context?: string;
}

const DEFAULT_TIMEOUT = 30000; // 30 seconds
const DEFAULT_RETRIES = 0;

class ApiClient {
  private async requestWithRetry(
    url: string,
    options: RequestOptions,
    retries: number
  ): Promise<Response> {
    const controller = new AbortController();
    const timeout = options.timeout || DEFAULT_TIMEOUT;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorData: any = null;
        try {
          const text = await response.text();
          errorData = text ? JSON.parse(text) : null;
        } catch {
          // Ignore JSON parse errors
        }

        throw new ApiError(
          response.status,
          response.statusText,
          errorData?.message || response.statusText,
          errorData
        );
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new TimeoutError(`Request timeout after ${timeout}ms`);
      }

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new NetworkError('Network error. Please check your connection.');
      }

      if (retries > 0 && this.isRetryableError(error)) {
        logger.warn(`Request failed, retrying... (${retries} retries left)`, error);
        await this.delay(1000 * (DEFAULT_RETRIES - retries + 1));
        return this.requestWithRetry(url, options, retries - 1);
      }

      throw error;
    }
  }

  private isRetryableError(error: unknown): boolean {
    if (error instanceof NetworkError || error instanceof TimeoutError) {
      return true;
    }

    if (error instanceof ApiError) {
      return error.status >= 500 || error.status === 429;
    }

    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async request<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.baseURL}${url}`;
    const retries = options.retries !== undefined ? options.retries : DEFAULT_RETRIES;
    const shouldDeduplicate = options.deduplicate !== false && options.method === 'GET';
    const deduplicationKey =
      options.deduplicationKey || (shouldDeduplicate ? `${options.method || 'GET'}:${fullUrl}` : undefined);

    const performRequest = async (): Promise<T> => {
      const response = await this.requestWithRetry(fullUrl, options, retries);
      const contentType = response.headers.get('content-type');

      let data: any;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (options.validate) {
        return validateResponse<T>(data, options.validate, options.context || url) as T;
      }

      return data as T;
    };

    if (deduplicationKey && shouldDeduplicate) {
      return deduplicateRequest<T>(deduplicationKey, performRequest);
    }

    return performRequest();
  }

  async requestData<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.baseURL}${url}`;
    const retries = options.retries !== undefined ? options.retries : DEFAULT_RETRIES;
    const shouldDeduplicate = options.deduplicate !== false && options.method === 'GET';
    const deduplicationKey =
      options.deduplicationKey || (shouldDeduplicate ? `${options.method || 'GET'}:${fullUrl}` : undefined);

    const performRequest = async (): Promise<T> => {
      const response = await this.requestWithRetry(fullUrl, options, retries);
      const contentType = response.headers.get('content-type');

      let data: any;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
        data = data.data !== undefined ? data.data : data;
      } else {
        data = await response.text();
      }

      if (options.validate) {
        return validateResponse<T>(data, options.validate, options.context || url) as T;
      }

      return data as T;
    };

    if (deduplicationKey && shouldDeduplicate) {
      return deduplicateRequest<T>(deduplicationKey, performRequest);
    }

    return performRequest();
  }

  get<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(url, { ...options, method: 'GET' });
  }

  post<T = any>(url: string, data?: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T = any>(url: string, data?: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T = any>(url: string, data?: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(url, { ...options, method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();

